/** Label when a stored staff id no longer resolves to a user record. */
export const UNASSIGNED_STAFF_LABEL = 'Unassigned'

/**
 * Split a stored full name ("Mary Jane Gomez") into first/last parts using
 * the last token as the last name — the same heuristic the schedule builder's
 * teacher dropdown sort uses. Users have a single `name` field, so anything
 * smarter would need a schema change.
 * @param {string} fullName
 * @returns {{ first: string, last: string }}
 */
export function splitFullName(fullName) {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
  if (parts.length <= 1) return { first: '', last: parts[0] || '' }
  return { first: parts.slice(0, -1).join(' '), last: parts[parts.length - 1] }
}

/**
 * "Gomez, Mary Jane" for sorted staff lists, matching the lastName sort order
 * so the alphabetical scan runs down the left edge (same convention as
 * formatStudentName's 'lastFirst'). Single-word names and emails pass through.
 * @param {{ name?: string, email?: string, id?: string }} user
 * @returns {string}
 */
export function formatUserLastFirst(user) {
  const full = (user?.name || user?.email || user?.id || '').trim()
  const { first, last } = splitFullName(full)
  return first ? `${last}, ${first}` : last
}

const AERIES_NAME_TITLE = /^(ms|mr|mrs|dr|miss)\.?$/i

/**
 * Capitalize the email local-part (`juann@…` → `Juann`). Last resort only —
 * Aeries already has the teacher name, and a schedule showing "Juann" instead
 * of "J. Nava" is how this fallback looks when it is used as the stored name.
 */
export function nameFromEmailLocal(email) {
  const local = String(email || '').split('@')[0] || ''
  return local
    .split(/[._-]+/)
    .filter(Boolean)
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Turn an Aeries teacher-name cell into the app's `users.name` shape
 * ("First Last"), so schedule cells can render "J. Nava".
 *
 * Aeries often exports `Last, First` or `Last, Ms. T`. A missing name falls
 * back to the email local-part so create-staff still has something editable.
 */
export function staffNameFromAeries(rawName, email = '') {
  const fallback = nameFromEmailLocal(email)
  const name = String(rawName || '').trim()
  if (!name) return fallback

  if (!name.includes(',')) return name

  const [lastPart, ...restParts] = name.split(',')
  const last = lastPart.trim()
  const restTokens = restParts.join(',').trim().split(/\s+/).filter(Boolean)
  const withoutTitle = restTokens.filter((t) => !AERIES_NAME_TITLE.test(t))
  if (!last) return name || fallback
  if (withoutTitle.length === 1 && /^[A-Za-z]\.?$/.test(withoutTitle[0])) {
    return `${withoutTitle[0].replace(/\.$/, '')}. ${last}`
  }
  if (withoutTitle.length) return `${withoutTitle.join(' ')} ${last}`
  return last
}

/**
 * Schedule / roster compact form: "J. Nava".
 * Strips a Google " - VUSD …" suffix and accepts `Last, First` if that is
 * what was stored. A single token (an email-local-part name) is returned as-is.
 */
export function formatTeacherScheduleName(userOrName) {
  const user =
    typeof userOrName === 'string' || userOrName == null ? { name: userOrName || '' } : userOrName
  const raw = String(user.name || '').trim()
  const cleaned = raw.replace(/\s+-\s+VUSD\b.*$/i, '').trim()
  const normalized = cleaned.includes(',') ? staffNameFromAeries(cleaned) : cleaned
  const parts = normalized.split(/\s+/).filter(Boolean)
  if (parts.length >= 2) return `${parts[0][0].toUpperCase()}. ${parts.slice(1).join(' ')}`
  if (parts.length === 1) return parts[0]
  if (user.email) return user.email[0].toUpperCase()
  return ''
}

/**
 * Comparator: last name, then first, case-insensitive. Falls back to email/id
 * when a name is missing so unnamed users still sort deterministically.
 */
export function compareUsersByLastName(a, b) {
  const key = (u) => {
    const { first, last } = splitFullName(u?.name || u?.email || u?.id || '')
    return `${last} ${first}`.toLowerCase()
  }
  return key(a).localeCompare(key(b))
}

/**
 * Resolve a staff id to a display name.
 * @param {string|null|undefined} userId
 * @param {Record<string, { name?: string, email?: string }>} userMap
 * @param {{
 *   knownMissing?: Set<string>,
 *   emptyLabel?: string,
 *   missingLabel?: string,
 * }} [opts]
 */
export function displayStaffName(userId, userMap = {}, opts = {}) {
  const { knownMissing = new Set(), emptyLabel = '', missingLabel = UNASSIGNED_STAFF_LABEL } = opts

  if (!userId) return emptyLabel
  const user = userMap[userId]
  if (user) return user.name || user.email || userId
  if (knownMissing.has(userId)) return missingLabel
  // Id exists but user is not in the school map yet (cross-school resolve) or was deleted.
  return missingLabel
}
